import React, { ComponentProps, FC, ReactNode } from 'react';

import FaqSection from '@rover/components/src/FaqSection';
import { normalizeLocale } from '@rover/constants/src/i18n/locale';
import HTMLBox from '@rover/kibble/patterns/HTMLBox';
import useTheme from '@rover/kibble/themes/useTheme';

export type LandingFaqItem = {
  question: string;
  answer: string;
  faqId?: number;
  // The FAQ's own locale (`it_IT`), which a fallback can make differ from the page's.
  locale?: string;
};

const INLINE_TAGS = 'a|b|em|i|strong|u';
const WHITESPACE_AFTER_INLINE_OPEN_TAG = new RegExp(`(<(?:${INLINE_TAGS})\\b[^>]*>)\\s+`, 'g');
const WHITESPACE_BEFORE_INLINE_CLOSE_TAG = new RegExp(`\\s+(</(?:${INLINE_TAGS})>)`, 'g');
const WHITESPACE_BEFORE_PUNCTUATION = new RegExp(`(</(?:${INLINE_TAGS})>)\\s+([.,;:!?)])`, 'g');

// The backend stores answers pretty-printed, one tag or string per line, so a link
// would render with a space inside it and another before the punctuation after it.
export const tightenInlineTagWhitespace = (html: string): string =>
  html
    .replace(WHITESPACE_AFTER_INLINE_OPEN_TAG, '$1')
    .replace(WHITESPACE_BEFORE_INLINE_CLOSE_TAG, '$1')
    .replace(WHITESPACE_BEFORE_PUNCTUATION, '$1$2');

export type LandingFaqListProps = Omit<ComponentProps<typeof FaqSection>, 'faqItems'> & {
  faqs: LandingFaqItem[];
  children?: ReactNode;
};

const LandingFaqList: FC<LandingFaqListProps> = ({ faqs, children, ...sectionProps }) => {
  const { paragraph } = useTheme();
  const bodyCopy = paragraph['200'];

  // Answers arrive as HTML strings, so their tags are bare DOM nodes rather than
  // Kibble components and would inherit the smaller global body font. Give them
  // the styles a Paragraph would have, including its reset of the global ones.
  // The answer fills its accordion panel, so the outer spacing is dropped.
  const answerSx = {
    fontSize: bodyCopy.fontSize,
    fontWeight: bodyCopy.fontWeight,
    lineHeight: bodyCopy.lineHeight,
    '& p': { padding: 0, margin: 0, border: '0 none', ...bodyCopy },
    '& a': { textDecoration: 'underline' },
    '& > :first-child': { marginTop: 0, paddingTop: 0 },
    '& > :last-child': { marginBottom: 0, paddingBottom: 0 },
  };

  if (faqs.length === 0) return null;

  return (
    <FaqSection
      {...sectionProps}
      faqItems={faqs.map(({ question, answer, faqId, locale }) => {
        const lang = normalizeLocale(locale);
        return {
          faqId,
          itemHeading: <span lang={lang}>{question}</span>,
          itemBody: (
            <div lang={lang}>
              <HTMLBox text={tightenInlineTagWhitespace(answer)} sx={answerSx} />
            </div>
          ),
        };
      })}
    >
      {children}
    </FaqSection>
  );
};

export default LandingFaqList;
